import React, { useMemo } from 'react';
import { Box, Button, makeStyles, Tooltip } from '@material-ui/core';
import { ImperativeHandle } from '../../shared/components/filter/Filter';
import { getColumns, ColumnNamesEnum } from './Columns';
import { Sort } from '../../shared/hooks/useSorting';
import ContextualMenu, {
    ContextualMenuItem,
} from '../../shared/components/contextualMenu/ContextualMenu';
import { ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

interface SortItemWithSortable {
    id: string;
    label: string;
    desc: boolean;
    sortable: true;
}

export type SortItem = SortItemWithSortable;

const useStyles = makeStyles((theme) => ({
    button: {
        borderStyle: 'solid',
        padding: theme.spacing(3 / 8, 1),
        borderWidth: theme.spacing(1 / 8),
        borderRadius: theme.spacing(4 / 8),
    },
    wrapper: {
        position: 'relative',
        padding: theme.spacing(12 / 8, 0),
        right: '0px',
        top: '0px',
    },
}));

interface Props {
    toggleSort: (id: string, desc: boolean) => void;
    sort: Sort | null;
}

const GraphicsSorter = React.forwardRef<ImperativeHandle, Props>(
    ({ toggleSort, sort }, ref) => {
        const classes = useStyles();
        const [anchorElement, setAnchorElement] =
            React.useState<HTMLElement | null>(null);
        const [clicked, setClicked] = React.useState<boolean>(false);
        const getDirectionIcon = (desc: boolean | null): any =>
            desc ? <ArrowDownward /> : <ArrowUpward />;
        const { t } = useTranslation();

        const columns = getColumns(t);

        const items: SortItem[] = [
            {
                label: columns[ColumnNamesEnum.dateAdd].label,
                id: columns[ColumnNamesEnum.dateAdd].name,
                desc: true,
                sortable: true,
            },
            {
                label: columns[ColumnNamesEnum.dateAdd].label,
                id: columns[ColumnNamesEnum.dateAdd].name,
                desc: false,
                sortable: true,
            },
            {
                label: columns[ColumnNamesEnum.name].label,
                id: columns[ColumnNamesEnum.name].name,
                desc: true,
                sortable: true,
            },
            {
                label: columns[ColumnNamesEnum.name].label,
                id: columns[ColumnNamesEnum.name].name,
                desc: false,
                sortable: true,
            },
        ];

        const singleLevelList = useMemo<
            ContextualMenuItem[]
        >((): ContextualMenuItem[] => {
            const output: ContextualMenuItem[] = [];

            items.forEach((item: SortItem) => {
                output.push({
                    text: item.label,
                    icon: getDirectionIcon(item.desc),
                    id: item.id + (item.desc ? 'desc' : 'asc'),
                    onClick: () => {
                        setAnchorElement(null);
                        setClicked(false);
                        toggleSort(item.id, item.desc);
                    },
                });
            });

            return output;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [items, clicked, setClicked]);

        const handleExpand = (event: React.MouseEvent<HTMLElement>): void => {
            event.stopPropagation();
            setClicked(true);
            setAnchorElement(event.currentTarget);
        };

        const handleCollapse = (): void => {
            setClicked(false);
            setAnchorElement(null);
        };

        return (
            <Box className={classes.wrapper}>
                <Tooltip title={String(t('Sort by'))}>
                    <Button onClick={handleExpand} className={classes.button}>
                        {sort && (
                            <>
                                {getDirectionIcon(sort.desc)}{' '}
                                {columns[sort.id].label}
                            </>
                        )}
                    </Button>
                </Tooltip>
                <ContextualMenu
                    anchorElement={anchorElement}
                    handleClose={handleCollapse}
                    singleLevelList={singleLevelList}
                    indexPrefix={'sorter'}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                />
            </Box>
        );
    }
);

export default GraphicsSorter;
